// AuthContext — Matrix v2 auth (port starého /AuthContext.jsx)
// API zhoda: ten istý backend /api/auth/login + /me + /logout, ten istý localStorage `kamir_token`.
// → frontend2 a starý frontend zdielajú session bez ďalšieho zariadenia.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { syncLang } from '../i18n.js'

const API = '/api'
const TOKEN_KEY = 'kamir_token'
const LAST_USER_KEY = 'kamir_last_user'

// Stav pohladu, ktory patri KONKRETNEMU cloveku, nie pocitacu. Kym sa tieto kluce
// nemazali, ostavali visiet aj po prihlaseni niekoho ineho — Ivanovi ostala zalozka
// Dopytov „Z WEB" a pod Luciinym kontom na jeho MacBooku tak videl len polovicu
// dopytov (4.9.2026). Zamerne sa NEMAZE vsetko: `kamir_v2_view` je nudzovy prepinac
// admina, QR base v Pozicovni je nastavenie tlaciarne na tom stroji a offline zoznam
// zavad moze niest este neodoslane data.
const OSOBNE_KLUCE = [
  'kamir_dopyty_tab',   // zalozka Z NEO / Z WEB — je za nou polovica dopytov
  'kamir_trinity_chat', // historia chatu s asistentom (firemne data + otazky cloveka)
  'zk_list_hidden',     // zbaleny bocny zoznam v Zakazkach
]
function zabudniOsobneNastavenia() {
  try { OSOBNE_KLUCE.forEach(k => localStorage.removeItem(k)) } catch (_) {}
}

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)        // { id, username, display_name, role, permissions, nav_permissions, language_code }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Restore session pri mount cez localStorage
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) { setLoading(false); return }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setUser(data); setLoading(false) })
      // vyprsana/neplatna relacia je tiez koniec relacie — inak by nastavenia
      // prezili do prihlasenia dalsieho cloveka
      .catch(() => { localStorage.removeItem(TOKEN_KEY); zabudniOsobneNastavenia(); setLoading(false) })
  }, [])

  // i18n: pri každom prihlásení stiahni/zaseeduj jazyk (ústava 264 auto-generátor)
  useEffect(() => { if (user && user.language_code) syncLang(user) }, [user])

  const login = useCallback(async (username, password) => {
    setError('')
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'Chyba prihlásenia'); return false }
      // Poistka, ked sa predosla relacia neskoncila cez Odhlasit (vyprsala, iny
      // prehliadac): ked na tento pocitac prichadza INY clovek, zacina nacisto.
      // Ten isty clovek si svoje nastavenia ponecha.
      const kto = (data.user && data.user.username) || ''
      if (localStorage.getItem(LAST_USER_KEY) !== kto) zabudniOsobneNastavenia()
      try { localStorage.setItem(LAST_USER_KEY, kto) } catch (_) {}
      localStorage.setItem(TOKEN_KEY, data.token)
      setUser(data.user)
      return true
    } catch (e) {
      setError('Nedá sa pripojiť k serveru')
      return false
    }
  }, [])

  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      try { await fetch(`${API}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }) } catch (_) {}
    }
    localStorage.removeItem(TOKEN_KEY)
    zabudniOsobneNastavenia()
    setUser(null)
  }, [])

  // Permission check (modul/akcia) — kompat s starým can()
  const can = useCallback((module, action = 'view') => {
    if (!user) return false
    if (user.role === 'admin') return true
    return !!user.permissions?.[module]?.[action]
  }, [user])

  // Nav section visibility
  const canNav = useCallback((key) => {
    if (!user) return false
    if (user.role === 'admin') return true
    const navPerms = user.nav_permissions
    if (!navPerms) return true
    const val = navPerms[key]
    return val === undefined ? true : !!val
  }, [user])

  const logAction = useCallback((action, module, entity_id = '', detail = '', reason = '') => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    fetch(`${API}/auth/audit-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, module, entity_id, detail, reason })
    }).catch(() => {})
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, error, setError, login, logout, can, canNav, logAction }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export default AuthContext
